<!--
This file is part of the project: "Digiziege"
An app for animal-welfare-oriented goat husbandry and contactless weight estimation.
GitLab: https://gitlab.rlp.net/proj-wise2526-digiziege/digiziege

Copyright (c) 2026 Digiziege Team TH-Bingen

Description:
Companion app for welfare-oriented goat husbandry featuring contactless weight estimation via camera,
master data management and health tracking.
Built as a PWA with a local-first approach to ensure data privacy.

LICENSE INFORMATION:
This program is free software: you can redistribute it and/or modify it under the terms of the
GNU Affero General Public License as published by the Free Software Foundation,
either version 3 of the License, or (at your option) any later version.

This program is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
See the GNU Affero General Public License for more details.

You should have received a copy of the GNU Affero General Public License along with this program.
If not, see <https://www.gnu.org/licenses/>.
-->

<template>
  <MoreItemWrapper
    :icon="isDark ? Moon : Sun"
    :title="$t('views.more.theme.toggle')"
  >
    <template #extra>
      <ToggleSwitch v-model="isDark" />
    </template>
  </MoreItemWrapper>
</template>

<script setup>
import { ref, watch, onMounted, onBeforeUnmount } from 'vue'
import { Sun, Moon } from 'lucide-vue-next'
import { useI18n } from 'vue-i18n'
import MoreItemWrapper from '@/components/settings/MoreItem.vue'
import ToggleSwitch from '@/components/ui/inputs/SwitchToggle.vue'

const { t } = useI18n()
const THEME_KEY = 'theme'
const defaultIsDark = true
const isDark = ref(defaultIsDark)

function applyTheme(val) {
  document.documentElement.setAttribute('data-theme', val ? 'dark' : 'light')
}

function readStoredTheme() {
  try {
    const stored = localStorage.getItem(THEME_KEY)
    if (stored === 'dark') return true
    if (stored === 'light') return false
  } catch {}
  const prefersDark = typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches
  return prefersDark ?? defaultIsDark
}

onMounted(() => {
  isDark.value = readStoredTheme()
  applyTheme(isDark.value)
  window.addEventListener('storage', onStorage)
})

onBeforeUnmount(() => {
  window.removeEventListener('storage', onStorage)
})

function onStorage(e) {
  if (e.key === THEME_KEY) isDark.value = e.newValue === 'dark'
}

watch(isDark, (val) => {
  applyTheme(val)
  try { localStorage.setItem(THEME_KEY, val ? 'dark' : 'light') } catch {}
})
</script>
